import { baseApi } from "./baseApi";
import { toast } from "react-toastify";

export type ArtistArtworkStatus = "pending" | "approved" | "rejected";

export type WaybackStatus =
  | "queued"
  | "requested"
  | "captured"
  | "failed"
  | "skipped";

export interface WaybackInfo {
  status: WaybackStatus;
  scheduledAt?: string;
  capturedAt?: string;
  snapshotUrl?: string;
  timestamp?: string;
  attempts: number;
  error?: string;
}

export interface ArtistInfo {
  _id: string;
  username: string;
  userEmoji?: string;
  displayName: string;
  theme: string;
  bio: string;
}

export interface ArtistArtwork {
  _id: string;
  title: string;
  description?: string;
  dateMade?: string;
  dateAdded: string;
  imageFilename?: string;
  imageContentSha256?: string;
  donation: boolean;
  status: ArtistArtworkStatus;
  standalonePageEnabled: boolean;
  wayback: WaybackInfo;
  artist: ArtistInfo;
}

export interface ContributorSummary {
  _id: string;
  username: string;
  userEmoji?: string;
  displayName: string;
  theme: string;
  bio: string;
  artworkCount: number;
}

export interface ArtistPagePayload {
  artist: Omit<ArtistInfo, "_id"> & { _id: string };
  artworks: ArtistArtwork[];
  viewerCanEdit: boolean;
}

export interface ContributorRecord {
  _id: string;
  username: string;
  userEmoji?: string;
  email: string;
  role: string;
  isContributor: boolean;
  artistProfile: {
    theme: string;
    displayName: string;
    bio: string;
  };
}

export interface ContributorUpdatePayload {
  isContributor?: boolean;
  theme?: string;
  displayName?: string;
  bio?: string;
}

export const artistsGalleryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getArtistsGallery: builder.query<ArtistArtwork[], void>({
      query: () => "/artists-gallery",
      providesTags: ["ArtistsGallery"],
    }),

    getContributors: builder.query<ContributorSummary[], void>({
      query: () => "/artists-gallery/contributors",
      providesTags: ["Contributors"],
    }),

    getArtistPage: builder.query<ArtistPagePayload, string>({
      query: (username) => `/artists-gallery/by-artist/${encodeURIComponent(username)}`,
      providesTags: (_r, _e, username) => [
        { type: "ArtistsGallery", id: username },
        "ArtistsGallery",
      ],
    }),

    getArtistArtwork: builder.query<ArtistArtwork, string>({
      query: (id) => `/artists-gallery/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ArtistsGallery", id }],
    }),

    getStandaloneArtwork: builder.query<ArtistArtwork, string>({
      query: (id) => `/artists-gallery/standalone/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ArtistsGallery", id }],
    }),

    addArtistArtwork: builder.mutation<{ artwork: ArtistArtwork }, FormData>({
      query: (formData) => ({
        url: "/artists-gallery/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["ArtistsGallery", "ArtistsGalleryPending"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Artwork submitted for review", {
            position: "bottom-right",
          });
        } catch {
          // caller handles error
        }
      },
    }),

    updateArtistArtwork: builder.mutation<
      ArtistArtwork,
      { id: string; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/artists-gallery/${id}`,
        method: "PATCH",
        body: formData,
      }),
      invalidatesTags: ["ArtistsGallery", "ArtistsGalleryPending"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Artwork updated", { position: "bottom-right" });
        } catch {
          // caller handles error
        }
      },
    }),

    deleteArtistArtwork: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/artists-gallery/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ArtistsGallery", "ArtistsGalleryPending"],
    }),

    toggleStandalonePage: builder.mutation<
      ArtistArtwork,
      { id: string; enabled: boolean }
    >({
      query: ({ id, enabled }) => ({
        url: `/artists-gallery/${id}/standalone`,
        method: "PATCH",
        body: { enabled },
      }),
      invalidatesTags: ["ArtistsGallery"],
      async onQueryStarted({ enabled }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success(
            enabled
              ? "Standalone page link enabled"
              : "Standalone page link hidden",
            { position: "bottom-right" }
          );
        } catch (err: any) {
          const msg =
            err?.error?.data?.message ||
            err?.data?.message ||
            "Failed to toggle standalone page";
          toast.error(msg, {
            position: "bottom-right",
          });
        }
      },
    }),

    retryWayback: builder.mutation<
      { _id: string; wayback: { status: WaybackStatus; scheduledAt?: string } },
      string
    >({
      query: (id) => ({
        url: `/artists-admin/artworks/${id}/wayback/retry`,
        method: "POST",
      }),
      invalidatesTags: ["ArtistsGallery", "ArtistsGalleryPending"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Wayback re-queued", { position: "bottom-right" });
        } catch {
          toast.error("Retry failed", { position: "bottom-right" });
        }
      },
    }),

    listContributors: builder.query<ContributorRecord[], void>({
      query: () => "/artists-admin/contributors",
      providesTags: ["Contributors"],
    }),

    updateContributor: builder.mutation<
      ContributorRecord,
      { username: string; payload: ContributorUpdatePayload }
    >({
      query: ({ username, payload }) => ({
        url: `/artists-admin/users/${encodeURIComponent(username)}`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["Contributors", "ArtistsGallery"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Contributor updated", { position: "bottom-right" });
        } catch {
          toast.error("Failed to update contributor", {
            position: "bottom-right",
          });
        }
      },
    }),

    getPendingArtworks: builder.query<ArtistArtwork[], void>({
      query: () => "/artists-admin/pending",
      providesTags: ["ArtistsGalleryPending"],
    }),

    reviewArtistArtwork: builder.mutation<
      { _id: string; status: ArtistArtworkStatus },
      { id: string; decision: "approve" | "reject"; note?: string }
    >({
      query: ({ id, decision, note }) => ({
        url: `/artists-admin/artworks/${id}/review`,
        method: "PATCH",
        body: { decision, note },
      }),
      invalidatesTags: ["ArtistsGallery", "ArtistsGalleryPending"],
      async onQueryStarted({ decision }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success(decision === "approve" ? "Approved" : "Rejected", {
            position: "bottom-right",
          });
        } catch {
          toast.error("Review failed", { position: "bottom-right" });
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetArtistsGalleryQuery,
  useGetContributorsQuery,
  useGetArtistPageQuery,
  useGetArtistArtworkQuery,
  useGetStandaloneArtworkQuery,
  useAddArtistArtworkMutation,
  useUpdateArtistArtworkMutation,
  useDeleteArtistArtworkMutation,
  useToggleStandalonePageMutation,
  useRetryWaybackMutation,
  useListContributorsQuery,
  useUpdateContributorMutation,
  useGetPendingArtworksQuery,
  useReviewArtistArtworkMutation,
} = artistsGalleryApi;
